import createSections from '@sdk/components/ui/anchorMenu/createSections';
import gettext from '@sdk/lang/gettext';
import { Feature } from '@sdk/common/features';
import { LaborCostCalculatorType } from '@sdk/common/types';
import type { Sections } from '@sdk/components/ui/anchorMenu/createSections';
import type { OrgSettings } from '@sdk/authentication/types/OrganisationType';
import type { AuthUserType } from '@sdk/authentication/types/AuthUserType';

import { TabType } from '../types';

export type AllSectionsConfig = {
  [TabType.preferences]: Sections;
  [TabType.notifications]: Sections;
  [TabType.companySettings]: Sections;
  [TabType.schedule]: Sections;
  [TabType.timeClock]: Sections;
  [TabType.laborCost]: Sections;
  [TabType.timeOff]: Sections;
};

const getAllSectionsConfig = (
  orgSettings: OrgSettings,
  currentUser: AuthUserType,
  isOrgFeatureEnabled: (feature: Feature) => boolean
): AllSectionsConfig => {
  const preferences = createSections([
    {
      label: gettext('Display preferences'),
      anchor: 'display',
    },
    {
      label: gettext('Other preferences'),
      anchor: 'other',
    },
  ]);

  const notifications = getNotificationSections(currentUser, orgSettings, isOrgFeatureEnabled);

  const companySettings = createSections([
    {
      label: gettext('Restrictions'),
      anchor: 'restrictions',
    },
    {
      label: gettext('Messages'),
      anchor: 'messages',
    },
    {
      label: gettext('Newsfeed'),
      anchor: 'newsfeed',
    },
    {
      label: gettext('Tasks'),
      anchor: 'tasks',
    },
  ]);

  const schedule = createSections([
    {
      label: gettext('Schedule settings'),
      anchor: 'scheduleSettings',
    },
    {
      label: gettext('Breaks'),
      anchor: 'breaks',
    },
    {
      label: gettext('Shift acceptance'),
      anchor: 'shiftAcceptance',
    },
    {
      label: gettext('Shift exchanges'),
      anchor: 'shiftExchanges',
    },
    {
      label: gettext('Shift feedback'),
      anchor: 'shiftFeedback',
    },
    {
      label: gettext('Unavailability'),
      anchor: 'unavailability',
    },
    {
      label: gettext('Labor compliance'),
      anchor: 'laborCompliance',
    },
  ]);

  const timeClock = getTimeClockSections(orgSettings);

  const laborCost = getLaborCostSections(orgSettings);

  const timeOff = createSections([
    {
      label: gettext('Time off requests'),
      anchor: 'timeOffRequests',
    },
    {
      label: gettext('Paid time off'),
      anchor: 'paidTimeOff',
    },
  ]);

  return {
    [TabType.preferences]: preferences,
    [TabType.notifications]: notifications,
    [TabType.companySettings]: companySettings,
    [TabType.schedule]: schedule,
    [TabType.timeClock]: timeClock,
    [TabType.laborCost]: laborCost,
    [TabType.timeOff]: timeOff,
  };
};

function getLaborCostSections(orgSettings: OrgSettings): Sections {
  const config = [
    {
      label: gettext('General settings'),
      anchor: 'generalSettings',
    },
  ];

  if (orgSettings.laborCostCalculatorType === LaborCostCalculatorType.us) {
    config.push(
      {
        label: gettext('Overtime'),
        anchor: 'overtime',
      },
      {
        label: gettext('Holiday pay'),
        anchor: 'holidayPay',
      },
      {
        label: gettext('Spread of hours'),
        anchor: 'spreadOfHours',
      }
    );
  }

  config.push(
    {
      label: gettext('Labor %'),
      anchor: 'labor',
    },
    {
      label: gettext('Labor cost access'),
      anchor: 'laborCostAccess',
    }
  );

  return createSections(config);
}

function getTimeClockSections(orgSettings: OrgSettings): Sections {
  const { clockInFromKiosk, clockInFromWebAndApps, clockInEnabled } = orgSettings;

  let config = [
    {
      label: gettext('General settings'),
      anchor: 'generalSettings',
    },
  ];

  if ((clockInFromWebAndApps && clockInEnabled) || clockInFromKiosk) {
    config.push(
      {
        label: gettext('Attendance'),
        anchor: 'attendance',
      },
      {
        label: gettext('Timesheets'),
        anchor: 'timesheets',
      },
      {
        label: gettext('Restrictions'),
        anchor: 'restrictions',
      },
      {
        label: gettext('Geofencing'),
        anchor: 'geofencing',
      }
    );

    if (!clockInFromWebAndApps) {
      config = config.filter((item) => item.anchor !== 'geofencing');
    }
  }

  if (clockInFromKiosk) {
    config.push({
      label: gettext('Kiosk sessions'),
      anchor: 'kioskSessions',
    });
  }

  return createSections(config);
}

function getNotificationSections(
  currentUser: AuthUserType,
  orgSettings: OrgSettings,
  isOrgFeatureEnabled: (feature: Feature) => boolean
): Sections {
  const showMessagesSection =
    !orgSettings.messagesFeatureDisabled && isOrgFeatureEnabled(Feature.messages);
  const showTimeClock =
    (orgSettings.clockInFromWebAndApps || orgSettings.clockInEnabled) &&
    isOrgFeatureEnabled(Feature.clockIn);

  const config = [
    {
      label: gettext('Shift alarms'),
      anchor: 'shiftAlarms',
    },
    {
      label: gettext('Dashboard'),
      anchor: 'dashboard',
    },
  ];

  if (showMessagesSection) {
    config.push({
      label: gettext('Messages'),
      anchor: 'messages',
    });
  }

  if (!orgSettings.newsfeedFeatureDisabled) {
    config.push({
      label: gettext('Newsfeed'),
      anchor: 'newsfeed',
    });
  }

  config.push({
    label: gettext('Daily report'),
    anchor: 'dailyReport',
  });

  if (showTimeClock) {
    config.push({
      label: gettext('Timeclock'),
      anchor: 'timeClock',
    });
  }

  return createSections(
    config.filter((section) => currentUser.isManager || section.anchor !== 'dailyReport')
  );
}

export default getAllSectionsConfig;
